import styled from 'styled-components';
import {
  TABLE_COLUMN_MAX_WIDTH,
  TABLE_COLUMN_MIN_WIDTH,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';

export const Container = styled.div`
  position: relative;
  margin: 1rem 0;
  font-family: Eesti, sans-serif;
  min-width: 0;
  max-width: 100%;
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 5px rgba(0, 0, 0, 0.04);
  border-radius: 1rem;
  min-width: 0;
  max-width: 100%;
`;

export const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 1.4rem;
`;

export const Thead = styled.thead`
  background-color: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

export const Tbody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

export const Tr = styled.tr``;

export const Th = styled.th<{ $align?: 'start' | 'end'; $isFrozen?: boolean }>`
  position: ${({ $isFrozen }) => ($isFrozen ? `sticky` : `relative`)};
  left: ${({ $isFrozen }) => ($isFrozen ? `0` : `auto`)};
  z-index: ${({ $isFrozen }) => ($isFrozen ? 3 : 1)};
  padding: 0;
  text-align: ${({ $align }) => ($align === `end` ? `right` : `left`)};
  font-weight: 600;
  color: #111;
  font-size: 1.4rem;
  min-width: ${TABLE_COLUMN_MIN_WIDTH};
  max-width: ${TABLE_COLUMN_MAX_WIDTH};
  width: auto;
  box-sizing: border-box;
  background-color: ${({ $isFrozen }) =>
    $isFrozen ? `#ffffff` : `transparent`};
  box-shadow: ${({ $isFrozen }) =>
    $isFrozen ? `4px 0 6px -4px rgba(0, 0, 0, 0.2)` : `none`};

  .tooltip-question-mark {
    background: transparent;
    border: 1px solid rgb(156 167 177);
    color: rgb(156 167 177);
    font-size: 1rem;
  }
`;

export const HeaderRow = styled.div<{ $align?: 'start' | 'end' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === `end` ? `flex-end` : `flex-start`};
  gap: 0.6rem;
  min-height: 4.6rem;
  width: 100%;
  min-width: 0;
  padding: 0.5rem 0rem 0.5rem 1.6rem;
  box-sizing: border-box;
  color: #111;
  cursor: grab;
  transition: background-color 150ms ease;
  touch-action: none;

  &:hover,
  &:focus-visible {
    background: rgba(0, 0, 0, 0.04);
  }

  &:active {
    cursor: grabbing;
  }
`;

export const HeaderLabel = styled.span<{ $align?: 'start' | 'end' }>`
  width: fit-content;
  min-width: 0;
  font-weight: 400;
  font-size: 1.2rem;
  color: rgb(119 129 139);
  white-space: break-spaces;
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.35;
  text-align: ${({ $align }) => ($align === `end` ? `right` : `left`)};
`;

export const SortIndicator = styled.span<{ $isSorted?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  flex: 0 0 1.8rem;
  color: ${({ $isSorted }) => ($isSorted ? `#111827` : `#9ca3af`)};
  font-size: 1.2rem;
  opacity: ${({ $isSorted }) => ($isSorted ? 1 : 0)};
  pointer-events: none;
  transition: opacity 150ms ease, color 150ms ease;

  ${Th}:hover &,
  ${Th}:focus-within & {
    opacity: 1;
  }
`;

export const ColumnResizer = styled.div`
  position: absolute;
  top: 0;
  right: -0.4rem;
  bottom: 0;
  z-index: 2;
  width: 0.8rem;
  cursor: col-resize;
  touch-action: none;
  opacity: 0;

  &::after {
    position: absolute;
    top: 0.4rem;
    bottom: 0.4rem;
    left: 50%;
    width: 1px;
    background: rgba(0, 0, 0, 0.1);
    content: '';
    transform: translateX(-50%);
    transition: background-color 150ms ease;
  }

  &:hover::after,
  &:active::after {
    background: rgba(37, 99, 235, 0.5);
  }
`;

export const Td = styled.td<{ $align?: 'start' | 'end'; $isFrozen?: boolean }>`
  position: ${({ $isFrozen }) => ($isFrozen ? `sticky` : `static`)};
  left: ${({ $isFrozen }) => ($isFrozen ? `0` : `auto`)};
  z-index: ${({ $isFrozen }) => ($isFrozen ? 2 : `auto`)};
  padding: 1.2rem 1.6rem;
  color: #333;
  line-height: 1.55;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  min-width: ${TABLE_COLUMN_MIN_WIDTH};
  max-width: ${TABLE_COLUMN_MAX_WIDTH};
  width: auto;
  box-sizing: border-box;
  text-align: ${({ $align }) => ($align === `end` ? `right` : `left`)};
  vertical-align: middle;
  background-color: ${({ $isFrozen }) =>
    $isFrozen ? `#ffffff` : `transparent`};
  box-shadow: ${({ $isFrozen }) =>
    $isFrozen ? `4px 0 6px -4px rgba(0, 0, 0, 0.12)` : `none`};
`;

export const ChartContainer = styled.div`
  width: 100%;
  height: 32rem;
  padding: 0rem 0.8rem 0;
  overflow: hidden;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  font-size: 1.2rem;
`;

export const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.2rem;
  padding-bottom: 1rem;
  flex-wrap: wrap;
`;

export const ViewControls = styled.div`
  display: inline-flex;
  border: 1px solid rgb(209 213 219 / 0.5);
  border-radius: 0.6rem;
  overflow: hidden;
`;

export const ViewButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: 0;
  border-right: 1px solid rgb(209 213 219 / 0.5);
  background: ${({ $active }) => ($active ? `rgb(236, 242, 252)` : `#fff`)};
  color: ${({ $active }) => ($active ? `rgb(52, 99, 232)` : `#111827`)};
  cursor: pointer;
  width: 4rem;
  height: 3rem;
  padding: 0.7rem 1rem;
  font-family: Eesti, sans-serif;
  font-size: 1.2rem;

  &:last-child {
    border-right: 0;
  }

  &:disabled {
    color: #9ca3af;
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

export const AxisControls = styled.div`
  display: inline-flex;
  align-items: stretch;
  border: 1px solid rgb(209 213 219 / 0.5);
  border-radius: 0.6rem;
  background: white;
  overflow: hidden;
  height: 3rem;
  max-width: 100%;
`;

export const AxisSegment = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  padding: 0 1.2rem;
  flex: 1 1 0;
  min-width: 14rem;
  color: #111827;
  font-family: Eesti, sans-serif;
  font-size: 1.2rem;
  cursor: pointer;
  user-select: none;

  &:not(:last-child) {
    border-right: 1px solid rgb(209 213 219 / 0.5);
  }

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

export const AxisLabelText = styled.span`
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  color: #111827;
  font-family: Eesti, sans-serif;
  font-size: 1.2rem;
  font-weight: 500;
  pointer-events: none;
`;

export const AxisTooltipWrapper = styled.span`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  .tooltip-question-mark {
    margin: 0;
  }
`;

export const AxisSelect = styled.select`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
  font-family: Eesti, sans-serif;
  font-size: 1.2rem;
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const SelectLabel = AxisSegment;
export const SelectLabelHeader = AxisLabelText;

export const ComparePieLayout = styled.div`
  display: flex;
  gap: 1.6rem;
  width: 100%;
  height: 100%;
  min-width: 0;
`;

export const ComparePiePanel = styled.div`
  display: flex;
  flex: 1 1 50%;
  flex-direction: column;
  min-width: 0;
`;

export const ComparePieTitle = styled.div`
  padding-bottom: 0.6rem;
  color: #4b5563;
  font-size: 1.1rem;
  font-weight: 700;
  text-align: center;
`;

export const PieChartLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  height: 100%;
  min-width: 0;
`;

export const PieChartArea = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: min(28rem, 100%);
  aspect-ratio: 1;
  min-width: 0;
`;

export const PieChartCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 6.5rem;
  max-width: calc(100% - 1rem);
  pointer-events: auto;
  transform: translate(-50%, -50%);
  text-align: center;

  > div {
    width: 100%;
    min-width: 0;
  }
`;

export const PieChartCenterValue = styled.span`
  overflow: hidden;
  color: #111827;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 2rem;
  font-weight: 700;
`;

export const PieChartCenterLabel = styled.span`
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  color: #6b7280;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 0.9rem;
  line-height: 1.1rem;
`;

export const PieChartCenterLabelWrapper = styled.span`
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;

  &:hover > div {
    visibility: visible;
    opacity: 1;
  }
`;

export const PieChartCenterTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 50%;
  z-index: 9999;
  visibility: hidden;
  width: max-content;
  padding: 0.8rem 1.2rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  background: #fff;
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  color: #374151;
  font-size: 1.5rem;
  line-height: 1.5rem;
  opacity: 0;
  pointer-events: none;
  transform: translateX(-50%);
  transition: opacity 120ms ease;
  white-space: nowrap;
`;

export const PieLegend = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  width: fit-content;
  min-width: 25rem;
  max-width: 30rem;
  overflow-y: auto;
  padding: 2rem;
  height: 100%;
`;

export const PieLegendList = styled.div`
  margin: auto 0;
`;

export const PieLegendHeader = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto auto;
  gap: 1.2rem;
  padding: 0 0.4rem 0.6rem;
  color: #6b7280;
  font-size: 1.1rem;
  font-weight: 600;
`;

export const PieLegendHeaderLabel = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const PieLegendSortButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #4338ca;
  cursor: pointer;
  font: inherit;
  font-size: 1.1rem;
  font-weight: 600;

  &:hover,
  &:focus-visible {
    color: #312e81;
  }
`;

export const PieLegendSortIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 1.1rem;
`;

export const PieLegendRow = styled.div<{ $dimmed?: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto auto;
  gap: 1.2rem;
  padding: 0.5rem 0.4rem;
  min-width: 0;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.5 : 1)};
  transition: opacity 150ms ease;
  cursor: default;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

export const PieLegendNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-width: 0;
`;

export const PieLegendSwatch = styled.span<{ $color: string }>`
  flex: 0 0 1rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

export const PieLegendName = styled.span`
  overflow: hidden;
  flex: 1 1 auto;
  min-width: 0;
  color: #374151;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 1.2rem;
`;

export const PieLegendValue = styled.span`
  flex: 0 0 auto;
  color: #111827;
  text-align: right;
  white-space: nowrap;
  font-size: 1.2rem;
`;

export const PieLegendShare = styled.span`
  flex: 0 0 auto;
  color: #6b7280;
  text-align: right;
  white-space: nowrap;
  font-size: 1.2rem;
`;

export const Select = AxisSelect;
